import { Toast } from '@byecode/ui'
import {
    DOWNLOAD_FILE_URL_LIST,
    errorCodeMap,
    limitExceededCodeList,
    notFoundCodeList,
    notFoundTemplateCodeList,
    noUserCodeList,
    skipCodeList,
    TokenRefreshManager,
    tokenStorage,
    useServiceMessageCodeList
} from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import type { AxiosInstance } from 'axios'
import axios from 'axios'
import Qs from 'qs'
import serialize from 'serialize-javascript'

import { openSpaceGrade } from './components/SpaceGrade/event'
import { QuotaMessage } from './components/SpaceQuota/QuotaMessage'
import { ERROR_CODE_QUOTA_KEY_MAP } from './components/SpaceQuota/QuotaMessage/constants'

/**
 * 配置项
 */
const API_BASE_URL = '/lighthouse'
const REFRESH_ENDPOINT = '/api/auth/v1/refreshToken' // POST -> { refreshToken: string } 返回 { accessToken, refreshToken? }

export interface HttpResp<T = undefined> {
    success: boolean
    msg: string
    code: string
    content: T
}

const http: AxiosInstance = axios.create({
    // timeout: 8000,
    withCredentials: true,
    baseURL: API_BASE_URL,
    headers: {
        'Request-Id': nanoid(20)
    },
    transformRequest: [
        (data, headers) => {
            if (headers?.['Content-Type'] === 'application/json') {
                return serialize(data, { space: 0, isJSON: true })
            }
            if (headers?.['Content-Type'] === 'multipart/form-data') {
                return data
            }
            return Qs.stringify(data)
        }
    ]
})

const getPath = () => {
    const { pathname, search } = window.location
    const url = new URLSearchParams()
    if (pathname === '/') {
        return [undefined, search]
    }
    return [pathname, search]
}

const redirectLogin = () => {
    const [pathname, search] = getPath()
    const redirect = pathname
    const searchParams = new URLSearchParams(search)
    if (redirect) {
        searchParams.set('redirect', redirect)
    }
    const params = searchParams.toString()
    window.location.href = `/account/login${params ? `?${params}` : ''}`
}

/**
 * 统一的 Token 刷新管理器实例
 */
export const tokenRefreshManager = new TokenRefreshManager({
    apiBaseUrl: API_BASE_URL,
    refreshEndpoint: REFRESH_ENDPOINT,
    redirectLoginFn: redirectLogin,
    httpInstance: http
})

http.interceptors.response.use(
    resp => {
        const { data, config } = resp ?? {}
        if (DOWNLOAD_FILE_URL_LIST.includes(config.url ?? '')) {
            return resp
        }
        const { code, msg } = data ?? {}

        if (notFoundCodeList.includes(code)) {
            window.location.href = '/404'
            return data
        }

        if (notFoundTemplateCodeList.includes(code)) {
            window.location.href = '/template/404'
            return data
        }

        if (skipCodeList.includes(code)) {
            return data
        }

        if (noUserCodeList.includes(code)) {
            return data
        }

        // if(noAccessCodeList.includes(code)) {
        //     redirectLogin()
        //     return data
        // }

        if (Object.hasOwn(ERROR_CODE_QUOTA_KEY_MAP, code)) {
            QuotaMessage({
                type: ERROR_CODE_QUOTA_KEY_MAP[code]
            })
            return data
        }
        if (limitExceededCodeList.includes(code)) {
            requestAnimationFrame(() => {
                Toast.error(`${errorCodeMap[code as keyof typeof errorCodeMap] || '未知错误'}，`, {
                    linkText: '请升级版本',
                    onLinkClick: () => openSpaceGrade('version_upgrade')
                })
            })
            return data
        }

        if (useServiceMessageCodeList.includes(code)) {
            Toast.error(msg)
            return data
        }
        const levelCode = code?.[2]
        // 暂时处理 jwt 失效，走完 refreshAuthLogic 逻辑后，未重新刷新，是因为进到下面 default，被 reject 了
        // 也是属于之前写这块逻辑的时候埋的坑，刷 token 跟后面这些拦截器本来就不应该走同一套拦截器
        if (!levelCode) {
            return Promise.resolve(resp)
        }

        switch (levelCode) {
            case '0': {
                return data
            }
            case '1': {
                const msg = errorCodeMap[code as keyof typeof errorCodeMap]
                if (msg) {
                    requestAnimationFrame(() => {
                        Toast.warning(msg)
                    })
                }
                return data
            }
            case '2': {
                requestAnimationFrame(() => {
                    Toast.error(errorCodeMap[code as keyof typeof errorCodeMap] || '未知错误')
                })
                return data
            }
            default: {
                const message = errorCodeMap[code as keyof typeof errorCodeMap]
                if (message) {
                    requestAnimationFrame(() => {
                        Toast.error(message)
                    })
                }
                return Promise.reject(data)
            }
        }
    },

    async error => {
        const { response, code, config } = error

        // 2024-07-12 12:01:28 当网络异常时，提示网络异常
        if (code === 'ERR_NETWORK') {
            Toast.error('网络异常，操作失败')
        }

        // 处理 401 错误，使用统一的 TokenRefreshManager
        if (response?.status === 401) {
            try {
                return await tokenRefreshManager.handleAxios401(config, http)
            } catch {
                // 刷新失败，重新抛出原始错误
                throw error
            }
        }

        if (axios.isCancel(error)) {
            return
        }

        throw error
    }
)

http.interceptors.request.use(config => {
    // 如果是 401 重试请求，跳过所有业务处理逻辑
    if (config.headers?.['X-Skip-Interceptor']) {
        delete config.headers['X-Skip-Interceptor'] // 清理标记避免发送到服务器
        return config
    }

    const rawUrl = config.url

    const token = tokenStorage.getAccessToken()
    if (token) {
        config.headers = config.headers ?? {}
        config.headers['Authorization'] = `Bearer ${token}`
    }

    if (config.headers?.['Content-Type'] === 'application/json' && typeof config.data === 'string') {
        // 只有在字符串不是 "undefined" 且是有效 JSON 时才 parse
        if (config.data !== 'undefined' && config.data.trim()) {
            try {
                config.data = JSON.parse(config.data)
            } catch {
                // 解析失败时保持原值
            }
        } else {
            // 如果是 "undefined" 或空字符串，则设置为 undefined
            config.data = undefined
        }
    }

    // 排除直接指定完整 url 请求外部接口的情况
    if (!rawUrl || rawUrl?.startsWith('http')) {
        return config
    }

    return config
})

export default http
